import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, MapPin, Clock, User, FileText,
  Camera, Navigation, Loader2, ExternalLink,
  AlertTriangle, CheckCircle2,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'
import { buildRoute } from '@/config/routes'
import WorkerLayout from '@/components/worker/WorkerLayout'
import Button from '@/components/ui/Button'

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_UI = {
  assigned:         { label: 'Assigned',        color: 'text-blue-400 bg-blue-500/10 border-blue-500/25'           },
  en_route:         { label: 'En Route',         color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25'    },
  in_progress:      { label: 'In Progress',      color: 'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25' },
  pending_approval: { label: 'Pending Approval', color: 'text-orange-400 bg-orange-500/10 border-orange-500/25'    },
}

function fmtDate(dateStr, time) {
  if (!dateStr) return '—'
  const d = new Date(dateStr + 'T00:00:00')
  const label = d.toLocaleDateString('en-PH', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
  return time ? `${label} · ${time}` : label
}

function firstNameOnly(fullName) {
  if (!fullName) return 'Client'
  return fullName.split(' ')[0]
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function JobDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)

  const [job,     setJob]     = useState(null)
  const [photos,  setPhotos]  = useState([])
  const [loading, setLoading] = useState(true)
  const [acting,  setActing]  = useState(false)
  const [err,     setErr]     = useState(null)
  const [available, setAvailable] = useState(false)

  useEffect(() => {
    if (!profile?.id) return
    fetchJob()
    fetchAvailability()
  }, [profile?.id, id]) // eslint-disable-line

  async function fetchAvailability() {
    const { data } = await supabase
      .from('worker_profiles')
      .select('is_available')
      .eq('id', profile.id)
      .maybeSingle()
    if (data) setAvailable(data.is_available ?? false)
  }

  async function fetchJob() {
    setLoading(true)
    setErr(null)
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        id, ref_number, status, description,
        address, address_notes, lat, lng,
        scheduled_date, scheduled_time,
        worker_payout,
        service_categories ( name ),
        profiles!bookings_customer_id_fkey ( full_name ),
        booking_photos ( id, photo_type, storage_path, caption )
      `)
      .eq('id', id)
      .eq('worker_id', profile.id)
      .single()

    if (error || !data) {
      setErr('Job not found or you do not have access.')
      setLoading(false)
      return
    }

    setJob(data)
    setPhotos(
      (data.booking_photos ?? []).filter((p) => p.photo_type === 'customer_submitted')
    )
    setLoading(false)
  }

  async function markEnRoute() {
    setActing(true)
    const { error } = await supabase
      .from('bookings')
      .update({ status: 'en_route' })
      .eq('id', id)
      .eq('worker_id', profile.id)
    if (error) { setActing(false); return }
    navigate(buildRoute.workerOnsite(id))
  }

  async function goToOnsite() {
    navigate(buildRoute.workerOnsite(id))
  }

  function openMaps() {
    if (!job) return
    const query = job.lat && job.lng
      ? `${job.lat},${job.lng}`
      : encodeURIComponent(job.address)
    window.open(`https://maps.google.com/?q=${query}`, '_blank', 'noopener')
  }

  function getPhotoUrl(path) {
    const { data: { publicUrl } } = supabase.storage.from('job-photos').getPublicUrl(path)
    return publicUrl
  }

  if (loading) {
    return (
      <WorkerLayout available={available}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
        </div>
      </WorkerLayout>
    )
  }

  if (err || !job) {
    return (
      <WorkerLayout available={available}>
        <div className="px-4 py-10 text-center max-w-sm mx-auto">
          <AlertTriangle className="w-8 h-8 text-siglat-red mx-auto mb-3" />
          <p className="text-sm text-ink-secondary">{err ?? 'Job not found.'}</p>
          <button
            onClick={() => navigate('/worker')}
            className="mt-4 text-sm text-siglat-teal underline underline-offset-2"
          >
            Back to dashboard
          </button>
        </div>
      </WorkerLayout>
    )
  }

  const ui = STATUS_UI[job.status] ?? { label: job.status, color: 'text-ink-muted bg-surface-overlay border-surface-border' }
  const category = job.service_categories?.name ?? 'Service'
  const customerFirstName = firstNameOnly(job.profiles?.full_name)

  return (
    <WorkerLayout available={available}>
      <div className="max-w-lg mx-auto px-4 py-5 flex flex-col gap-4">

        {/* ── Back ─────────────────────────────────────────────────────────── */}
        <button
          onClick={() => navigate('/worker')}
          className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink-primary transition-colors w-fit"
        >
          <ArrowLeft className="w-4 h-4" />
          Dashboard
        </button>

        {/* ── Header card ──────────────────────────────────────────────────── */}
        <div className="bg-surface-raised border border-surface-border rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-base font-bold text-ink-primary">{category}</p>
              <p className="text-[10px] text-ink-muted font-mono mt-0.5">{job.ref_number}</p>
            </div>
            <span className={`text-[10px] px-2.5 py-1 rounded-full border font-semibold shrink-0 ${ui.color}`}>
              {ui.label}
            </span>
          </div>

          {/* Rows */}
          <div className="mt-1 flex flex-col gap-2">
            <InfoRow icon={User} label={`Client: ${customerFirstName}`} />
            <InfoRow icon={Clock} label={fmtDate(job.scheduled_date, job.scheduled_time)} />
            <button
              onClick={openMaps}
              className="flex items-start gap-2.5 text-left hover:opacity-80 transition-opacity"
            >
              <MapPin className="w-4 h-4 text-siglat-teal mt-0.5 shrink-0" />
              <p className="text-sm text-siglat-teal underline underline-offset-2 leading-snug">
                {job.address}
              </p>
              <ExternalLink className="w-3 h-3 text-siglat-teal shrink-0 mt-1" />
            </button>
            {job.address_notes && (
              <p className="text-xs text-ink-muted pl-6 leading-snug">{job.address_notes}</p>
            )}
          </div>
        </div>

        {/* ── Scope / description ───────────────────────────────────────────── */}
        <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-ink-muted" />
            <p className="text-xs font-semibold text-ink-secondary uppercase tracking-wide">Job Description</p>
          </div>
          <p className="text-sm text-ink-primary leading-relaxed whitespace-pre-wrap">{job.description}</p>
        </div>

        {/* ── Customer photos ───────────────────────────────────────────────── */}
        {photos.length > 0 && (
          <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Camera className="w-4 h-4 text-ink-muted" />
              <p className="text-xs font-semibold text-ink-secondary uppercase tracking-wide">
                Photos from client ({photos.length})
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p) => (
                <img
                  key={p.id}
                  src={getPhotoUrl(p.storage_path)}
                  alt={p.caption ?? 'Client photo'}
                  className="w-full aspect-square object-cover rounded-lg bg-surface-overlay"
                />
              ))}
            </div>
          </div>
        )}

        {/* ── Payout preview ────────────────────────────────────────────────── */}
        {job.worker_payout != null && (
          <div className="bg-siglat-teal/8 border border-siglat-teal/25 rounded-xl p-4 flex items-center justify-between">
            <p className="text-sm text-ink-secondary">Your payout for this job</p>
            <p className="text-base font-bold text-siglat-teal">
              ₱{Number(job.worker_payout).toLocaleString('en-PH', { minimumFractionDigits: 0 })}
            </p>
          </div>
        )}

        {/* ── CTA ──────────────────────────────────────────────────────────── */}
        <div className="pt-1">
          {job.status === 'assigned' && (
            <Button
              onClick={markEnRoute}
              disabled={acting}
              className="w-full py-4 text-base"
            >
              {acting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</>
                : <><Navigation className="w-4 h-4" /> I'm On My Way</>
              }
            </Button>
          )}

          {(job.status === 'en_route' || job.status === 'in_progress') && (
            <Button
              onClick={goToOnsite}
              className="w-full py-4 text-base"
            >
              <CheckCircle2 className="w-4 h-4" />
              {job.status === 'en_route' ? "I've Arrived — Check In" : 'Continue Job'}
            </Button>
          )}

          {job.status === 'pending_approval' && (
            <div className="text-center py-3">
              <p className="text-sm text-ink-muted">Waiting for admin approval on additional work.</p>
            </div>
          )}
        </div>

      </div>
    </WorkerLayout>
  )
}

function InfoRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="w-4 h-4 text-ink-muted shrink-0" />
      <p className="text-sm text-ink-secondary">{label}</p>
    </div>
  )
}
